import React, { useMemo } from 'react';
import LoadingFallback from 'containers/antifraud/loading-fallback';
import { Title, BizonSidebar } from 'components/commons';
import DocumentStatus from './components/document-status/document-status';
import { CompanyInfo } from 'generated/credit-analysis-graphql';
import { registerTagmanagerEvent, TagManagerEvent } from 'configs/trackers';
import { getLoanApplicationParams } from 'utils/utils';
import { useGetLoanQuery } from 'generated/graphql';

type DocumentCheckoutProps = {
    companyInfo: CompanyInfo;
    nextScreen(): void;
};

const DocumentCheckout: React.FC<DocumentCheckoutProps> = ({ nextScreen, companyInfo }): JSX.Element => {
    const loanApplicationId = getLoanApplicationParams();

    const { data, loading } = useGetLoanQuery({
        variables: {
            id: loanApplicationId
        },
        fetchPolicy: "network-only"
    });

    const checklistLoading = companyInfo === undefined;

    const isCompleted = useMemo(() => {
        return companyInfo && companyInfo.isChecklistUploadCompleted
    }, [companyInfo]);

    const { checklist, signers } = companyInfo;

    const handleRegisterEvent = () => {
        registerTagmanagerEvent(TagManagerEvent.APPROVED);
        nextScreen();
    };

    const itemsChecklist = checklist.items.concat(
        signers.reduce((array, signer) => signer.checklist.items, [])
    ).filter(item => item.externalNotification === true);

    return (
        <>
            <div className="bizon-checkout grid-container">
                <BizonSidebar
                    loanApplication={data?.loanApplication}
                    initialClosed={true}
                    className={`bizon-checkout__sidebar ${isCompleted && 'bizon-checkout__sidebar-completed'}`}
                    mainTitle={isCompleted ? undefined : <>falta pouco! não deixe sua <br /> oferta pré-aprovada expirar</>}
                    boxTitle={isCompleted ? <>resumo do seu pedido 🌟</> : <>caso tenha dúvidas, entre em contato pelos canais abaixo:</>}
                    isCompleted={isCompleted}
                />
                <div className={`bizon-checkout__content  ${isCompleted && 'bizon-checkout__content--completed'}`}>
                    {isCompleted
                        ? (
                            <>
                                <Title className="mt-4 lowercase">
                                    pronto! seu pedido de crédito está sendo analisado 🎉
                                </Title>
                                <p className="body-1 mt-3">
                                    Em breve vamos entrar em contato por e-mail.
                                </p>
                            </>
                        )
                        : <LoadingFallback
                            loading={loading && checklistLoading}
                            render={() => (
                                <>
                                    <Title className="mt-4 lowercase">
                                        {itemsChecklist.length > 0 ?
                                            'você precisa reenviar documentos' : 'hora de enviar documentos'}
                                    </Title>

                                    <p className="body-1 mt-3">
                                        {itemsChecklist.length > 0 ?
                                            'Envie novamente o documento que está como pendente para finalizarmos a análise de crédito.' :
                                            'Envie digitalmente alguns documentos da empresa pra gente terminar a análise de crédito.'
                                        }
                                    </p>
                                    <DocumentStatus companyInfo={companyInfo} />
                                </>
                            )}
                        />
                    }
                </div>
                <div className={['bizon-checkout__footer', isCompleted ? 'bizon-checkout__footer--sm-hide' : null].join(' ')}>
                    {!isCompleted && (
                        <button
                            type="button"
                            disabled={loading || checklistLoading}
                            className="btn-biz primary"
                            onClick={nextScreen}
                        >
                            vamos começar
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default DocumentCheckout;
